import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { NextConditionData } from "@/model";
import { toSpliced, withArray } from "@/util/arrayUtil";
import { getNewCondition } from "@/util/helper/paragraphHelper";
import { useSimpleScenes } from "@/query/scene";
import { useScenarioId, useScenarioAutocompletes } from "@/hook";
import Button from "@/component/Button";
import { Textarea } from "@/component/input";
import { SortableItemWrapper, SortableWrapper, SortableItem, toSortableItem } from "@/component/SortableWrapper";
import { DeleteIcon, AddIcon } from "@/assets/svgs/icons";

import { SceneTypeahead } from ".";


interface NextConditionSortableListProps {
    required?: boolean;
    className?: string;
    defaultConditions: NextConditionData[];
    onChangeConditions: (nextConditions: NextConditionData[]) => void;
}

const NextConditionSortableList: React.FC<NextConditionSortableListProps> = ({
    required = true,
    className: propsClassName,
    defaultConditions,
    onChangeConditions,
}) => {
    const intl = useIntl();
    const scenarioId = useScenarioId();
    const { fullAutocomplete } = useScenarioAutocompletes();

    const [sortableConditions, setSortableConditions] = useState<SortableItem<NextConditionData>[]>([]);

    const { data: scenes } = useSimpleScenes(scenarioId);

    const update = (nextConditions: SortableItem<NextConditionData>[]) => {
        setSortableConditions(nextConditions);
        onChangeConditions(nextConditions.map(item => item.data));
    };

    const onChangeCondition = (index: number, condition: NextConditionData) => {
        const prevCondition = sortableConditions[index];
        const newCondition = { ...prevCondition, data: condition };
        update(withArray(sortableConditions, index, newCondition));
    };

    useEffect(() => {
        const sortableConditions = defaultConditions.map(toSortableItem);
        if (sortableConditions.length === 0 && required) {
            sortableConditions.push(toSortableItem(getNewCondition()));
        }
        setSortableConditions(sortableConditions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div data-testid="paragraph-next-cond"
             className={propsClassName}
        >
            <div className="flex items-center gap-1">
                <div className="field-label">
                    {intl.formatMessage({ id: "i04024" })}
                </div>
                <Button variant="text-transparent"
                        aria-label={intl.formatMessage({ id: "b0009" })}
                        onClick={() => update([...sortableConditions, toSortableItem(getNewCondition())])}
                >
                    <AddIcon className="size-3" />
                </Button>
            </div>
            <SortableWrapper items={sortableConditions}
                             className="flex flex-col gap-2"
                             onDragSortEnd={update}
            >{sortableConditions.map((condition, index) => (
                <SortableItemWrapper key={condition.id}
                                     item={condition}
                                     aria-label={`${intl.formatMessage({ id: "i04024" })} ${index + 1}`}
                >{dragHandle => (
                        <div className="w-full flex items-start gap-1">
                            <div className="label-input-grid-label">
                                {dragHandle}
                            </div>
                            <Textarea rows={1}
                                      required
                                      aria-label={`${intl.formatMessage({ id: "i04024" })} ${index + 1}`}
                                      autocompletes={fullAutocomplete}
                                      defaultValue={condition.data.condition as string}
                                      placeholder={intl.formatMessage({ id: "i04076" })}
                                      onChange={value => onChangeCondition(index, { ...condition.data, condition: value })}
                            />
                            <SceneTypeahead scenes={scenes ?? []}
                                            customId={condition.data.next}
                                            onChange={customId => onChangeCondition(index, { ...condition.data, next: customId })}
                            />
                            <Button variant="text-transparent"
                                    className="label-input-grid-label ml-1.5 mr-2 flex-none disabled:opacity-0 disabled:cursor-default"
                                    disabled={required && sortableConditions.length <= 1}
                                    aria-label={intl.formatMessage({ id: "b0006" })}
                                    onClick={() => update(toSpliced(sortableConditions, index, 1))}
                            >
                                <DeleteIcon className="text-point-red size-4" />
                            </Button>
                        </div>
                )}</SortableItemWrapper>
            ))}</SortableWrapper>
        </div>
    );
};

export default NextConditionSortableList;
